import React, { useState, useEffect } from "react";

function ViewProjects() {
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [roundStatus, setRoundStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Form state for editing
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editNoOfInterns, setEditNoOfInterns] = useState("");
  const [editDuration, setEditDuration] = useState("");
  const [editMode, setEditMode] = useState("");
  const [editPrerequisites, setEditPrerequisites] = useState("NONE");
  const [editDepartmentId, setEditDepartmentId] = useState("");

  const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem("access_token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers
    };
    const response = await fetch(url, { ...options, headers });
    return response;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch projects
        const projectsRes = await fetchWithAuth("/api/professors/projects");
        if (projectsRes.ok) {
          const projectsData = await projectsRes.json();
          setProjects(projectsData);
        }

        // Fetch departments and normalize user_id to id
        const deptRes = await fetchWithAuth("/api/professors/department_list");
        if (deptRes.ok) {
          const deptData = await deptRes.json();
          const formattedDepartments = Array.isArray(deptData)
            ? deptData.map(dept => ({
                id: dept.user_id,
                name: dept.name
              }))
            : [];
          setDepartments(formattedDepartments);
        }

        // Fetch round status
        const roundRes = await fetchWithAuth("/api/professors/round_details");
        if (roundRes.ok) {
          const roundData = await roundRes.json();
          setRoundStatus(roundData);
        }
      } catch (error) {
        setMessage("Error fetching data");
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to normalize duration to match the select options
  const normalizeDuration = (duration) => {
    if (!duration) return "";
    
    // Convert to string and trim
    const durationStr = String(duration).trim();
    
    // Check if it's a number (like "1" or 1)
    if (!isNaN(durationStr)) {
      const num = parseInt(durationStr);
      return num === 1 ? "1 month" : `${num} months`;
    }
    
    // Handle "1 month" (lowercase) -> "1 Month" (capital M)
    if (durationStr.toLowerCase().includes("month")) {
      const num = durationStr.split(" ")[0];
      const parsedNum = parseInt(num);
      if (!isNaN(parsedNum)) {
        return parsedNum === 1 ? "1 month" : `${parsedNum} months`;
      }
    }
    
    // If it's already in the correct format, return as is
    if (durationStr === "1 month" || durationStr === "2 months") {
      return durationStr;
    }
    
    // Default fallback
    return durationStr;
  };
  const normalizeMode = (mode) => {
    if (!mode) return "";
    
    const modeStr = String(mode).trim().toLowerCase();
    if (modeStr === "online || ONLINE ") return "ONLINE";
    if (modeStr === "offline || OFFLINE") return "OFFLINE";
    if (modeStr === "hybrid || HYBRID") return "HYBRID";
    
    // Default fallback
    return mode;
  };

  const handleEditClick = (project) => {
    setEditingId(project.id);
    setEditTitle(project.title);
    setEditDescription(project.description);
    setEditNoOfInterns(project.no_of_interns);
    setEditDuration(normalizeDuration(project.duration)); // Normalize the duration
    setEditMode(normalizeMode(project.mode)); // Normalize the mode
    setEditPrerequisites(project.prerequisites || "NONE");
    setEditDepartmentId(project.dept_id);
  };

  const handleEditSubmit = async (e, id) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Validate form fields
    if (!editTitle || !editDescription || !editNoOfInterns || !editDuration || !editMode || !editDepartmentId) {
      setMessage("Please fill all required fields");
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetchWithAuth(`/api/professors/projects/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          title: editTitle,
          description: editDescription,
          no_of_interns: parseInt(editNoOfInterns),
          duration: editDuration, // Send the exact string from select (e.g., "1 Month")
          mode: editMode,
          prerequisites: editPrerequisites,
          dept_id: editDepartmentId
        })
      });

      const data = await res.json();

      if (res.ok) {
        setProjects(prev =>
          prev.map(p => p.id === id ? data : p)
        );
        setMessage("Project updated successfully!");
        setEditingId(null);
      } else {
        let errorMessage = "Failed to update project";
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(err => err.msg).join(", ");
        } else if (data.detail?.msg) {
          errorMessage = data.detail.msg;
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        setMessage(errorMessage);
      }
    } catch (error) {
      setMessage("Network error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    try {
      const res = await fetchWithAuth(`/api/professors/project/${id}`, {
        method: "DELETE"
      });

      if (res.status === 204) {
        setProjects(prev => prev.filter(p => p.id !== id));
        setMessage("Project deleted successfully!");
      } else {
        const data = await res.json();
        let errorMessage = "Failed to delete project";
        if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(err => err.msg).join(", ");
        } else if (data.detail?.msg) {
          errorMessage = data.detail.msg;
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
        setMessage(errorMessage);
      }
    } catch (error) {
      setMessage("Network error");
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => String(d.id) === String(deptId));
    return dept ? dept.name : "N/A";
  };

  return (
    <div className="view-projects">
      <h2>Your Projects</h2>
      {message && (
        <p style={{
          marginTop: "10px",
          color: message.includes("success") ? "green" : "red"
        }}>
          {typeof message === "string"
            ? message
            : message && message.msg
              ? message.msg
              : JSON.stringify(message)}
        </p>
      )}

      {isLoading && projects.length === 0 ? (
        <p>Loading projects...</p>
      ) : projects.length === 0 ? (
        <p>No projects found</p>
      ) : (
        <div className="projects-list">
          {projects.map(project => (
            <div key={project.id} className="project-card">
              {editingId === project.id ? (
                <div className="edit-project-form">
                  <h3>Edit Project</h3>
                  <form onSubmit={(e) => handleEditSubmit(e, project.id)}>
                    <div className="form-group">
                      <label>Title*</label>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Description*</label>
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Number of Interns*</label>
                      <input
                        type="number"
                        value={editNoOfInterns}
                        onChange={(e) => setEditNoOfInterns(e.target.value)}
                        min="1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Duration*</label>
                      <select
                        value={editDuration}
                        onChange={(e) => setEditDuration(e.target.value)}
                        required
                      >
                        <option value="">Select Duration</option>
                        <option value="1 month">1 month</option>
                        <option value="2 months">2 months</option>
                        {/* Add more options as needed */}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Mode*</label>
                      <select
                        value={editMode}
                        onChange={(e) => setEditMode(e.target.value)}
                        required
                      >
                        <option value="">Select Mode</option>
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                        <option value="Hybrid">Hybrid</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Department*</label>
                      <select
                        value={editDepartmentId}
                        onChange={(e) => setEditDepartmentId(e.target.value)}
                        required
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Prerequisites</label>
                      <input
                        type="text"
                        value={editPrerequisites}
                        onChange={(e) => setEditPrerequisites(e.target.value)}
                      />
                    </div>

                    <div className="form-actions">
                      <button
                        type="submit"
                        className="save-btn"
                        disabled={isLoading}
                      >
                        {isLoading ? "Saving..." : "Save Changes"}
                      </button>
                      <button
                        type="button"
                        className="cancel-btn"
                        onClick={() => setEditingId(null)}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <>
                  <div className="project-header">
                    <h3>{project.title}</h3>
                  </div>

                  <p>{project.description}</p>

                  <div className="project-details">
                    <div><strong>Interns:</strong> {project.no_of_interns}</div>
                    <div><strong>Vacancy:</strong> {project.vacancy_remaining}</div>
                    <div><strong>Duration:</strong> {project.duration}</div>
                    <div><strong>Mode:</strong> {project.mode}</div>
                    <div><strong>Department:</strong> {getDepartmentName(project.dept_id)}</div>
                    <div><strong>Prerequisites:</strong> {project.prerequisites || "None"}</div>
                    <div><strong>Created in Round:</strong> {project.created_round}</div>
                  </div>

                  <div className="project-actions">
                    <button
                      className="edit-btn"
                      onClick={() => handleEditClick(project)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(project.id)}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ViewProjects;